import { ArrowUpToLine, Film, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogTitle } from "@/components/ui/dialog"
import { cn } from "@/lib"

interface SelectedVideoFile {
  id: string
  file: File
  preview: string
  hasError: boolean
  errorMessage?: string
}

interface VideoUploadModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload: (file: File) => void
  maxFileSize?: number
  allowedTypes?: string[]
  title?: string
  isUploading?: boolean
  existingVideo?: boolean
}

const DEFAULT_MAX_FILE_SIZE = 100 * 1024 * 1024
const DEFAULT_ALLOWED_TYPES = ["video/mp4", "video/quicktime"]

export function VideoUploadModal({
  open,
  onOpenChange,
  onUpload,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  allowedTypes = DEFAULT_ALLOWED_TYPES,
  title = "Upload Introduction Video",
  isUploading = false,
  existingVideo = false,
}: VideoUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<SelectedVideoFile | null>(null)
  const [isDragActive, setIsDragActive] = useState(false)
  const [wasUploading, setWasUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const dragCounterRef = useRef(0)

  useEffect(() => {
    if (!open) {
      setSelectedFile(null)
      setIsDragActive(false)
      dragCounterRef.current = 0
    }
  }, [open])

  const validateFile = useCallback(
    (file: File): { valid: boolean; message?: string } => {
      if (!allowedTypes.includes(file.type)) {
        const allowedExts = allowedTypes.map(t => t.split("/")[1].toUpperCase()).join(", ")
        return { valid: false, message: `Invalid type. Allowed: ${allowedExts}` }
      }

      if (file.size > maxFileSize) {
        const maxSizeMB = maxFileSize / (1024 * 1024)
        return { valid: false, message: `Exceeds ${maxSizeMB}MB limit` }
      }

      return { valid: true }
    },
    [allowedTypes, maxFileSize],
  )

  const addFile = useCallback(
    (files: FileList | File[]) => {
      const fileArray = Array.from(files)
      const file = fileArray[0]

      if (!file) return

      if (selectedFile) {
        toast.error("Only 1 video can be uploaded")
        return
      }

      const validation = validateFile(file)
      const preview = URL.createObjectURL(file)

      setSelectedFile({
        id: `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file,
        preview,
        hasError: !validation.valid,
        errorMessage: validation.message,
      })
    },
    [selectedFile, validateFile],
  )

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current++
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragActive(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounterRef.current--
    if (dragCounterRef.current === 0) {
      setIsDragActive(false)
    }
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setIsDragActive(false)
      dragCounterRef.current = 0

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFile(e.dataTransfer.files)
      }
    },
    [addFile],
  )

  const handleRemoveFile = useCallback(() => {
    if (selectedFile?.preview) {
      URL.revokeObjectURL(selectedFile.preview)
    }
    setSelectedFile(null)
  }, [selectedFile])

  const handleUpload = useCallback(() => {
    if (!selectedFile || selectedFile.hasError) {
      toast.error("No valid video to upload")
      return
    }

    onUpload(selectedFile.file)
  }, [selectedFile, onUpload])

  useEffect(() => {
    if (wasUploading && !isUploading) {
      if (selectedFile?.preview) {
        URL.revokeObjectURL(selectedFile.preview)
      }
      setSelectedFile(null)
      onOpenChange(false)
    }
    setWasUploading(isUploading)
  }, [isUploading, selectedFile, onOpenChange, wasUploading])

  if (!open) return null

  const hasFile = selectedFile !== null && !selectedFile.hasError

  const formatFileSize = (bytes: number) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`
    return `${(bytes / 1024).toFixed(0)}KB`
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(",")}
        onChange={e => {
          if (e.target.files) {
            addFile(e.target.files)
            e.target.value = ""
          }
        }}
        className="hidden"
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-auto" preventCloseOnOutsideClick>
          <DialogTitle className="text-[20px] font-semibold leading-none mt-1 leading-[30px]">
            {title}
          </DialogTitle>

          <div className="overflow-auto">
            {selectedFile && (
              <div className="mb-4">
                <div className="mb-3 flex items-center justify-between">
                  <p className="text-[11px] font-medium">Selected video</p>
                </div>

                <div
                  className={cn(
                    "group relative rounded-xl overflow-hidden",
                    selectedFile.hasError ? "border-red" : "border-border",
                  )}
                >
                  <div className="flex items-center gap-3 bg-muted rounded-xl p-4">
                    {selectedFile.preview ? (
                      <video
                        src={selectedFile.preview}
                        className="h-16 w-28 rounded-lg object-cover bg-black"
                        muted
                      />
                    ) : (
                      <div className="h-16 w-28 rounded-lg flex items-center justify-center bg-black/10">
                        <Film className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary truncate">
                        {selectedFile.file.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(selectedFile.file.size)}
                      </p>
                      {selectedFile.hasError && (
                        <p className="text-xs text-red mt-1">{selectedFile.errorMessage}</p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation()
                        handleRemoveFile()
                      }}
                      className={cn(
                        "rounded-full p-2 transition-colors cursor-pointer",
                        selectedFile.hasError
                          ? "bg-red/80 hover:bg-red"
                          : "bg-red/60 hover:bg-red/80",
                      )}
                      title="Remove"
                    >
                      <X className="h-4 w-4 text-white" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div
              onClick={() => fileInputRef.current?.click()}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={cn(
                "flex flex-col items-center justify-center gap-2 rounded-xl cursor-pointer transition-all duration-200 py-6 px-6 bg-water-light border-1 border-water-light border-dashed",
                isDragActive ? "border-primary bg-primary/5" : "",
                selectedFile ? "mt-6 mb-4" : "mt-0",
              )}
            >
              <div className="bg-water p-2.5 rounded-full">
                <ArrowUpToLine
                  className={cn("h-5 w-5", isDragActive ? "text-primary" : "text-primary")}
                />
              </div>
              <p className="text-primary font-medium my-0 text-[11px] leading-[25px] font-semibold">
                {isDragActive ? "Drop video here" : "Drag & Drop your video here"}
              </p>
              <p className="text-[11px] text-muted-foreground">or</p>
              <Button variant="outline" size="sm" className="mt-1 mb-4 border-white">
                Browse Files
              </Button>
              <p className="text-[10px] text-body font-semibold">
                {existingVideo ? "Replace current video. " : "Up to 1 video. "}
                {allowedTypes.map(t => t.split("/")[1].toUpperCase()).join(", ")} up to{" "}
                {(maxFileSize / 1024 / 1024).toFixed(0)}MB
              </p>
            </div>
          </div>

          {selectedFile && (
            <DialogFooter className="">
              <Button
                onClick={handleUpload}
                className="px-18 py-0 h-[40px] uppercase text-[11px]"
                disabled={!hasFile || isUploading}
                isLoading={isUploading}
              >
                {existingVideo ? "Replace Video" : "Save"}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
